import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IUser } from '../types/auth.types';
import { CREATOR_PATHS } from '../config/creatorProgramPaths';
import { NavPersona, resolveNavPersona } from '../config/userNavMenu';
import { buildNavbarChrome, type NavbarChrome } from '../config/navbarChrome';

type NavPersonaContextValue = {
  /** Role used for account dropdown / sheet (buyer, seller, owner, staff). */
  menuPersona: NavPersona | 'guest';
  /** @deprecated Use menuPersona — kept for existing consumers. */
  activePersona: NavPersona | 'guest';
  chrome: NavbarChrome;
  workPersona: NavPersona;
  switchToShopping: () => void;
  switchToWork: () => void;
};

const NavPersonaContext = createContext<NavPersonaContextValue | null>(null);

export const NavPersonaProvider: React.FC<{
  children: React.ReactNode;
  isAuthenticated: boolean;
  user: IUser | null;
}> = ({ children, isAuthenticated, user }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const roles = user?.roles;

  const menuPersona: NavPersona | 'guest' = !isAuthenticated || !roles
    ? 'guest'
    : resolveNavPersona(roles, location.pathname);

  const workPersona = menuPersona === 'guest' ? 'buyer' : menuPersona;

  const chrome = buildNavbarChrome({
    isAuthenticated,
    pathname: location.pathname,
  });

  const switchToShopping = useCallback(() => {
    navigate('/shop');
  }, [navigate]);

  const switchToWork = useCallback(() => {
    const persona = resolveNavPersona(roles, location.pathname);
    const hub =
      persona === 'seller'
        ? '/seller/dashboard'
        : persona === 'creator'
          ? CREATOR_PATHS.dashboard
          : persona === 'owner' || persona === 'staff'
            ? '/owner'
            : '/account/dashboard';
    navigate(hub);
  }, [navigate, roles, location.pathname]);

  const value = useMemo(
    () => ({
      menuPersona,
      activePersona: menuPersona,
      chrome,
      workPersona: workPersona as NavPersona,
      switchToShopping,
      switchToWork,
    }),
    [menuPersona, chrome, workPersona, switchToShopping, switchToWork],
  );

  return (
    <NavPersonaContext.Provider value={value}>
      {children}
    </NavPersonaContext.Provider>
  );
};

export function useNavPersona(): NavPersonaContextValue {
  const ctx = useContext(NavPersonaContext);
  if (!ctx) {
    throw new Error('useNavPersona must be used within NavPersonaProvider');
  }
  return ctx;
}
